import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import * as FRAGS from "@thatopen/fragments";
import { FalloDeVisor } from "./errores.js";
import { PlanoDeSeccion } from "./seccion.js";
import type { Depuracion } from "./depuracion.js";
import type { Eje } from "./ui.js";

export interface OpcionesVisor {
  contenedor: HTMLElement;
  depuracion?: Depuracion | undefined;
}

export type EtapaCarga = FRAGS.LoadProgressEvent["stage"];

/** Direccion de la vista por defecto: tres cuartos, ligeramente desde arriba. */
const VISTA = new THREE.Vector3(1, 0.65, 1).normalize();

export class Visor {
  private readonly contenedor: HTMLElement;
  private readonly depuracion: Depuracion | undefined;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly escena = new THREE.Scene();
  private readonly camara: THREE.PerspectiveCamera;
  private readonly controles: OrbitControls;
  private readonly reloj = new THREE.Clock();
  private readonly fragments: FRAGS.FragmentsModels;
  private readonly luzDireccional: THREE.DirectionalLight;
  private readonly luzHemisferica: THREE.HemisphereLight;
  private readonly rejilla: THREE.GridHelper;
  private readonly suelo: THREE.Mesh<THREE.PlaneGeometry, THREE.ShadowMaterial>;
  private readonly seccion: PlanoDeSeccion;
  private readonly observadorTamano: ResizeObserver;

  private modelo: FRAGS.FragmentsModel | null = null;
  private cajaModelo = new THREE.Box3();
  private raf = 0;
  private cuadroPendiente = false;
  private actualizando = false;
  private actualizacionPendiente = false;
  private destruido = false;

  constructor(opciones: OpcionesVisor) {
    this.contenedor = opciones.contenedor;
    this.depuracion = opciones.depuracion;

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
      // El degradado de fondo lo pinta CSS detras del canvas: cero draw calls.
      alpha: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearAlpha(0);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    // El modelo es estatico: el shadow map se recalcula solo cuando cambia la geometria
    // visible, no en cada cuadro. Sin esto cada frame pagaria un segundo pase completo.
    this.renderer.shadowMap.autoUpdate = false;
    this.renderer.shadowMap.needsUpdate = true;
    this.contenedor.append(this.renderer.domElement);

    this.camara = new THREE.PerspectiveCamera(55, 1, 0.1, 1000);
    this.camara.position.set(10, 10, 10);

    this.controles = new OrbitControls(this.camara, this.renderer.domElement);
    this.controles.enableDamping = true;
    this.controles.dampingFactor = 0.08;
    this.controles.screenSpacePanning = false;
    this.controles.maxPolarAngle = Math.PI * 0.98;
    this.controles.addEventListener("change", () => {
      this.solicitarCuadro();
      void this.actualizarFragmentos();
    });

    this.luzHemisferica = new THREE.HemisphereLight(0xf1f5fc, 0x9aa5b4, 1.35);
    this.escena.add(this.luzHemisferica);

    this.luzDireccional = new THREE.DirectionalLight(0xffffff, 2.4);
    this.luzDireccional.castShadow = true;
    this.luzDireccional.shadow.mapSize.set(2048, 2048);
    this.luzDireccional.shadow.bias = -0.0005;
    this.luzDireccional.shadow.normalBias = 0.02;
    this.escena.add(this.luzDireccional);
    this.escena.add(this.luzDireccional.target);

    this.rejilla = new THREE.GridHelper(1, 1, 0x94a3b8, 0xcbd5e1);
    const materialRejilla = this.rejilla.material as THREE.Material;
    materialRejilla.transparent = true;
    materialRejilla.opacity = 0.5;
    this.escena.add(this.rejilla);

    this.suelo = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShadowMaterial({ opacity: 0.22 }),
    );
    this.suelo.rotation.x = -Math.PI / 2;
    this.suelo.receiveShadow = true;
    this.escena.add(this.suelo);

    // Sin argumento, el constructor usa `new URL("./Worker/worker.mjs", import.meta.url)`,
    // que Vite resuelve y emite como asset propio. El worker sale del mismo dominio y en
    // la misma version que la libreria; nunca se toca unpkg en runtime.
    this.fragments = new FRAGS.FragmentsModels();

    this.seccion = new PlanoDeSeccion(
      this.renderer,
      this.escena,
      this.camara,
      this.renderer.domElement,
      () => {
        this.renderer.shadowMap.needsUpdate = true;
        this.solicitarCuadro();
      },
      (arrastrando) => {
        // Sin esto, arrastrar el gizmo tambien orbitaria la camara.
        this.controles.enabled = !arrastrando;
      },
    );

    this.observadorTamano = new ResizeObserver(() => this.ajustarTamano());
    this.observadorTamano.observe(this.contenedor);
    this.ajustarTamano();

    const gl = this.renderer.getContext();
    this.depuracion?.detectarGpu(gl);
  }

  // ----------------------------------------------------------------- carga

  async cargar(
    bytes: Uint8Array,
    modelId: string,
    onEtapa: (etapa: EtapaCarga, fraccion: number) => void,
  ): Promise<void> {
    let modelo: FRAGS.FragmentsModel;
    try {
      modelo = await this.fragments.load(bytes, {
        modelId,
        onProgress: (evento) => onEtapa(evento.stage, evento.progress),
      });
    } catch (causa) {
      throw new FalloDeVisor("modelo_corrupto", causa);
    }

    this.modelo = modelo;
    // Obligatorio: es lo que habilita el culling y el LOD por camara.
    modelo.useCamera(this.camara);
    this.escena.add(modelo.object);

    await this.actualizarFragmentos(true);

    this.cajaModelo = modelo.box.clone();
    if (this.cajaModelo.isEmpty()) {
      throw new FalloDeVisor("modelo_corrupto");
    }

    this.ajustarEscena();
    this.encuadrar();
  }

  /** Dimensiona rejilla, suelo, sombra y plano de seccion segun la caja del modelo. */
  private ajustarEscena(): void {
    const centro = this.cajaModelo.getCenter(new THREE.Vector3());
    const tamano = this.cajaModelo.getSize(new THREE.Vector3());
    const radio = Math.max(tamano.length() * 0.5, 1);
    const lado = Math.max(tamano.x, tamano.z) * 2.2;

    this.rejilla.geometry.dispose();
    const divisiones = Math.max(4, Math.min(60, Math.round(lado / pasoRejilla(lado))));
    const nueva = new THREE.GridHelper(lado, divisiones, 0x94a3b8, 0xcbd5e1);
    this.rejilla.geometry = nueva.geometry;
    nueva.material.dispose();
    this.rejilla.position.set(centro.x, this.cajaModelo.min.y, centro.z);

    this.suelo.scale.set(lado, lado, 1);
    this.suelo.position.set(centro.x, this.cajaModelo.min.y - radio * 0.001, centro.z);

    this.luzDireccional.position
      .copy(centro)
      .add(new THREE.Vector3(0.6, 1.4, 0.9).multiplyScalar(radio));
    this.luzDireccional.target.position.copy(centro);
    const sombra = this.luzDireccional.shadow.camera;
    sombra.left = -radio * 1.2;
    sombra.right = radio * 1.2;
    sombra.top = radio * 1.2;
    sombra.bottom = -radio * 1.2;
    sombra.near = radio * 0.05;
    sombra.far = radio * 6;
    sombra.updateProjectionMatrix();

    this.seccion.ajustarAlModelo(this.cajaModelo);
    this.renderer.shadowMap.needsUpdate = true;
  }

  // --------------------------------------------------------------- acciones

  encuadrar(): void {
    if (this.cajaModelo.isEmpty()) return;
    const centro = this.cajaModelo.getCenter(new THREE.Vector3());
    const distancia = this.distanciaDeEncuadre(centro);

    this.camara.position.copy(centro).addScaledVector(VISTA, distancia);
    this.camara.near = Math.max(distancia / 5000, 0.01);
    this.camara.far = distancia * 12;
    this.camara.updateProjectionMatrix();

    this.controles.target.copy(centro);
    this.controles.update();
    // La sombra no se toca: es una luz direccional anclada al modelo, asi que su
    // shadow map no depende de donde este la camara.
    this.solicitarCuadro();
    void this.actualizarFragmentos();
  }

  /**
   * Distancia minima a la que la caja entera entra en el frustum.
   *
   * Se proyectan las ocho esquinas sobre los ejes de la camara en vez de usar el radio
   * de la esfera envolvente: con modelos anchos y bajos —un terreno, una planta— la
   * esfera mide la diagonal completa y deja el modelo diminuto en el centro.
   */
  private distanciaDeEncuadre(centro: THREE.Vector3): number {
    const fovV = THREE.MathUtils.degToRad(this.camara.fov);
    const fovH = 2 * Math.atan(Math.tan(fovV / 2) * this.camara.aspect);
    const tanV = Math.tan(fovV / 2);
    const tanH = Math.tan(fovH / 2);

    const derecha = new THREE.Vector3().crossVectors(VISTA, this.camara.up);
    if (derecha.lengthSq() < 1e-8) derecha.set(1, 0, 0);
    derecha.normalize();
    const arriba = new THREE.Vector3().crossVectors(derecha, VISTA).normalize();

    const min = this.cajaModelo.min;
    const max = this.cajaModelo.max;
    const esquina = new THREE.Vector3();
    let distancia = 0;

    for (let i = 0; i < 8; i++) {
      esquina.set(
        (i & 1) === 0 ? min.x : max.x,
        (i & 2) === 0 ? min.y : max.y,
        (i & 4) === 0 ? min.z : max.z,
      );
      esquina.sub(centro);
      const profundidad = esquina.dot(VISTA);
      const lateral = Math.abs(esquina.dot(derecha));
      const vertical = Math.abs(esquina.dot(arriba));
      distancia = Math.max(distancia, profundidad + lateral / tanH, profundidad + vertical / tanV);
    }

    // 4 % de aire para que el modelo no quede pegado a los bordes.
    return Math.max(distancia * 1.04, 0.5);
  }

  alternarRejilla(visible: boolean): void {
    this.rejilla.visible = visible;
    this.solicitarCuadro();
  }

  alternarSeccion(activa: boolean): void {
    this.seccion.alternar(activa);
  }

  fijarEjeSeccion(eje: Eje): void {
    this.seccion.fijarEje(eje);
  }

  // ------------------------------------------------------------- render loop

  /**
   * Render on-demand. No hay `requestAnimationFrame` continuo: se pide un cuadro
   * cuando la camara se mueve, cuando `fragments.update()` resuelve o cuando cambia
   * algo de la escena, y la cadena se corta sola en cuanto el damping se asienta.
   */
  private solicitarCuadro(): void {
    if (this.cuadroPendiente || this.destruido) return;
    this.cuadroPendiente = true;
    this.raf = requestAnimationFrame(this.dibujar);
  }

  private readonly dibujar = (): void => {
    this.cuadroPendiente = false;
    if (this.destruido) return;

    const delta = this.reloj.getDelta();
    const enMovimiento = this.controles.update(delta);

    // three lo pone en false al terminar el pase, asi que hay que leerlo antes.
    const conSombra = this.renderer.shadowMap.needsUpdate;
    this.renderer.render(this.escena, this.camara);

    const info = this.renderer.info;
    this.depuracion?.registrarCuadro({
      drawCalls: info.render.calls,
      triangulos: info.render.triangles,
      programas: info.programs?.length ?? 0,
      conSombra,
    });

    // El damping sigue moviendo la camara despues de soltar: se encadenan cuadros
    // hasta que se asienta, y ahi el loop se detiene.
    if (enMovimiento) this.solicitarCuadro();
  };

  private async actualizarFragmentos(forzar = false): Promise<void> {
    if (this.destruido) return;
    if (this.actualizando) {
      this.actualizacionPendiente = true;
      return;
    }
    this.actualizando = true;

    const inicio = performance.now();
    const promesa = this.fragments.update(forzar);
    // Lo que importa del presupuesto es cuanto bloquea el hilo principal, no el total.
    const bloqueo = performance.now() - inicio;
    try {
      await promesa;
    } catch {
      // Un update fallido no debe tumbar el visor; el siguiente movimiento lo reintenta.
    }
    this.depuracion?.registrarActualizacion({
      bloqueoMs: bloqueo,
      totalMs: performance.now() - inicio,
    });

    this.actualizando = false;
    if (this.destruido) return;

    // Clave para el presupuesto de draw calls: `actualizarFragmentos` corre en cada
    // movimiento de camara, y marcar la sombra aca sin condicion hacia que todos los
    // cuadros de una orbita pagaran el pase extra completo.
    if (this.prepararSombras()) this.renderer.shadowMap.needsUpdate = true;
    this.solicitarCuadro();

    if (this.actualizacionPendiente) {
      this.actualizacionPendiente = false;
      void this.actualizarFragmentos();
    }
  }

  /**
   * Los tiles llegan por streaming, asi que hay que marcarlos al asentarse cada update.
   * Devuelve si aparecio geometria nueva, que es lo unico que obliga a repintar el
   * shadow map.
   */
  private prepararSombras(): boolean {
    let nuevos = false;
    this.modelo?.object.traverse((objeto) => {
      if (objeto instanceof THREE.Mesh && !objeto.castShadow) {
        objeto.castShadow = true;
        objeto.receiveShadow = true;
        nuevos = true;
      }
    });
    return nuevos;
  }

  private ajustarTamano(): void {
    const ancho = this.contenedor.clientWidth;
    const alto = this.contenedor.clientHeight;
    if (ancho === 0 || alto === 0) return;
    this.camara.aspect = ancho / alto;
    this.camara.updateProjectionMatrix();
    this.renderer.setSize(ancho, alto, false);
    this.solicitarCuadro();
  }

  // ------------------------------------------------------------------ limpieza

  async dispose(): Promise<void> {
    if (this.destruido) return;
    this.destruido = true;

    cancelAnimationFrame(this.raf);
    this.observadorTamano.disconnect();
    this.controles.dispose();
    this.seccion.dispose();

    if (this.modelo !== null) {
      this.escena.remove(this.modelo.object);
      this.modelo = null;
    }
    // Cierra los workers y libera la geometria de los tiles.
    await this.fragments.dispose();

    this.rejilla.geometry.dispose();
    (this.rejilla.material as THREE.Material).dispose();
    this.suelo.geometry.dispose();
    this.suelo.material.dispose();
    this.luzDireccional.shadow.dispose();

    this.escena.clear();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
    this.renderer.domElement.remove();
  }
}

/** Paso de rejilla redondeado a 1/2/5 x 10^n para que las divisiones queden legibles. */
function pasoRejilla(lado: number): number {
  const bruto = lado / 20;
  const magnitud = 10 ** Math.floor(Math.log10(bruto));
  const normalizado = bruto / magnitud;
  const escalon = normalizado < 1.5 ? 1 : normalizado < 3.5 ? 2 : normalizado < 7.5 ? 5 : 10;
  return escalon * magnitud;
}
